"use client"

import { useState, useEffect } from "react"
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Chip,
  Grid,
  ListItemIcon,
} from "@mui/material"
import { Download, CheckCircle } from "@mui/icons-material"

// TODO: load from /static/templates or LLM endpoint
const dummyIndustryTemplates = {
  Manufacturing: [
    { id: "manu_intro", title: "Introduction & Company Overview" },
    { id: "manu_gov", title: "Governance & CSRD Strategy" },
    { id: "manu_imp", title: "Impact, Risk & Opportunity Assessment (ESRS 2 IRO)" },
    { id: "manu_env", title: "Environmental Disclosures (ESRS E1-E5)" },
    { id: "manu_soc", title: "Social Disclosures (ESRS S1-S4)" },
    { id: "manu_gov_g", title: "Governance Disclosures (ESRS G1)" },
    { id: "manu_conc", title: "Conclusion & Future Outlook" },
  ],
  Finance: [
    { id: "fin_intro", title: "Introduction & Financial Institution Profile" },
    { id: "fin_gov", title: "Governance & Sustainable Finance Strategy" },
    { id: "fin_imp", title: "Impact, Risk & Opportunity Assessment (ESRS 2 IRO)" },
    { id: "fin_cli", title: "Climate-Related Disclosures (ESRS E1 & Finance Specifics)" },
    { id: "fin_inv", title: "Investment & Lending Portfolio Impacts (ESRS S2, S3)" },
    { id: "fin_soc", title: "Other Social & Governance Disclosures (ESRS S1, S4, G1)" },
    { id: "fin_conc", title: "Conclusion & Forward-Looking Statements" },
  ],
  Technology: [
    { id: "tech_intro", title: "Introduction & Tech Company Profile" },
    { id: "tech_gov", title: "Governance & Digital Responsibility" },
    { id: "tech_imp", title: "Impact, Risk & Opportunity Assessment (ESRS 2 IRO)" },
    { id: "tech_env", title: "Environmental Impact (Data Centers, E-waste - ESRS E1-E5)" },
    { id: "tech_soc", title: "Social Impact (Digital Divide, Misinformation - ESRS S1-S4)" },
    { id: "tech_gov_g", title: "Ethical AI & Data Governance (ESRS G1)" },
    { id: "tech_conc", title: "Conclusion & Innovation Outlook" },
  ],
  Retail: [
    { id: "retail_intro", title: "Introduction & Retail Company Profile" },
    { id: "retail_gov", title: "Governance & Sustainable Retail Strategy" },
    { id: "retail_imp", title: "Impact, Risk & Opportunity Assessment (ESRS 2 IRO)" },
    { id: "retail_env", title: "Environmental Impact (Supply Chain, Packaging - ESRS E1-E5)" },
    { id: "retail_soc", title: "Social Impact (Labor Rights, Consumer Safety - ESRS S1-S4)" },
    { id: "retail_gov_g", title: "Supply Chain Governance (ESRS G1)" },
    { id: "retail_conc", title: "Conclusion & Market Outlook" },
  ],
}

const BaselineTemplate = () => {
  const industries = Object.keys(dummyIndustryTemplates)
  const [selectedIndustry, setSelectedIndustry] = useState("")
  const [templateSections, setTemplateSections] = useState([])

  useEffect(() => {
    if (industries.length > 0 && !selectedIndustry) {
      const firstIndustry = industries[0]
      setSelectedIndustry(firstIndustry)
      setTemplateSections(dummyIndustryTemplates[firstIndustry] || [])
    }
  }, [industries, selectedIndustry])

  const handleIndustryChange = (event) => {
    const industry = event.target.value
    setSelectedIndustry(industry)
    setTemplateSections(dummyIndustryTemplates[industry] || [])
  }

  const handleDownload = () => {
    alert(
      "Download .docx functionality to be implemented for: " +
        selectedIndustry +
        "\nThis would involve generating a document based on the selected sections.",
    )
    // TODO: Implement actual DOCX generation and download logic
  }

  return (
    <Box sx={{ p: 2, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: "#407775",
            fontWeight: "bold",
            mb: 1,
          }}
        >
          Baseline Report Template Generator
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#707070",
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          Generate industry-specific CSRD report templates with pre-structured sections and compliance guidelines
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Industry Selection - Full Width */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#407775",
                    fontWeight: "bold",
                  }}
                >
                  Select Industry & Generate Template
                </Typography>
                {selectedIndustry && (
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    startIcon={<Download />}
                    disabled={!selectedIndustry}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#407775",
                      "&:hover": {
                        backgroundColor: "#2E5266",
                      },
                      borderRadius: "8px",
                    }}
                  >
                    Download Template
                  </Button>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel
                    sx={{
                      "&.Mui-focused": {
                        color: "#407775",
                      },
                    }}
                  >
                    Industry Type
                  </InputLabel>
                  <Select
                    value={selectedIndustry}
                    label="Industry Type"
                    onChange={handleIndustryChange}
                    sx={{
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#407775",
                      },
                    }}
                  >
                    {industries.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedIndustry && (
                  <Chip
                    label={`${templateSections.length} sections available`}
                    sx={{
                      backgroundColor: "#f0f7f7",
                      color: "#407775",
                      border: "1px solid #407775",
                      fontWeight: "medium",
                    }}
                  />
                )}
              </Box>

              {selectedIndustry && templateSections.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "#407775",
                      fontWeight: "bold",
                      mb: 2,
                    }}
                  >
                    Report Outline: {selectedIndustry}
                  </Typography>

                  <List sx={{ p: 0 }}>
                    {templateSections.map((section, index) => (
                      <ListItem
                        key={section.id}
                        sx={{
                          border: "1px solid #f0f0f0",
                          borderRadius: "8px",
                          mb: 1,
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "white",
                          "&:hover": {
                            backgroundColor: "#f0f7f7",
                            borderColor: "#407775",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <CheckCircle sx={{ color: "#407775" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "medium",
                                color: "#707070",
                              }}
                            >
                              {index + 1}. {section.title}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default BaselineTemplate
